import { useEffect, useState } from "react";

/**
 * Application theme options matching @homesite/ui's ThemeProvider.
 * Defined here to avoid importing from @homesite/ui (which would create a cycle).
 */
export type AppTheme = "light" | "dark" | "high-contrast";

const STORAGE_KEY = "homesite-theme";

/**
 * Reads and writes the application theme, persisting the selection to localStorage.
 * On mount the hook reads localStorage; defaults to 'light' if no value is stored.
 */
export function useTheme(): {
	theme: AppTheme;
	setTheme: (t: AppTheme) => void;
} {
	const [theme, setThemeState] = useState<AppTheme>(() => {
		if (typeof window === "undefined") return "light";
		const stored = window.localStorage.getItem(STORAGE_KEY);
		return isAppTheme(stored) ? stored : "light";
	});

	useEffect(() => {
		if (typeof window === "undefined") return;
		window.localStorage.setItem(STORAGE_KEY, theme);
	}, [theme]);

	return {
		theme,
		setTheme: setThemeState,
	};
}

function isAppTheme(value: string | null): value is AppTheme {
	return value === "light" || value === "dark" || value === "high-contrast";
}
